import { BALANCE } from '../balance.ts';
import type { Rng } from '../rng.ts';
import type { PlayerId, PuzzleAct, PuzzleInstance, Role } from '../types.ts';
import {
  failResult,
  okResult,
  shown,
  type ActContext,
  type GenContext,
  type PuzzleModule,
  type SolveInfo,
  type TamperChoice,
  type ViewContext,
} from './types.ts';
import { alterableClues, clueByKind } from './util.ts';

/**
 * Vault code (GDD §8.9). Five symbols; every player privately knows one or two of them and
 * has to tell the others. The keypad is shared and the code is submitted only when every
 * thief confirms, so a liar can only lie about their own symbols. The vault tolerates two
 * wrong codes; the third locks it and the heist fails.
 */

export const CODE_LENGTH = 5;

export interface VaultParams {
  alphabet: number;
  code: number[];
  /** Positions each player knows, by player id. */
  owners: Record<PlayerId, number[]>;
}

export interface VaultState {
  entered: (number | null)[];
  confirms: PlayerId[];
  errors: number;
  failed: boolean;
}

const ALPHABET_BY_LEVEL = [6, 6, 6, 7, 8, 8] as const;

const params = (p: PuzzleInstance) => p.params as VaultParams;
const state = (p: PuzzleInstance) => p.state as VaultState;

/** How many positions each of the `n` players gets, GDD §8.9: 2/2/1, 2/1/1/1, 1/1/1/1/1. */
export function shareSizes(n: number): number[] {
  if (n === 3) return [2, 2, 1];
  if (n === 4) return [2, 1, 1, 1];
  return [1, 1, 1, 1, 1];
}

function generate(ctx: GenContext) {
  const { rng, level } = ctx;
  const alphabet = ALPHABET_BY_LEVEL[level] ?? 7;
  const code = Array.from({ length: CODE_LENGTH }, () => rng.int(0, alphabet - 1));
  const players = ctx.players.filter((p) => !p.expelled);
  const positions = rng.shuffle([0, 1, 2, 3, 4]);
  const sizes = rng.shuffle(shareSizes(players.length));
  const owners: Record<PlayerId, number[]> = {};
  let cursor = 0;
  players.forEach((pl, i) => {
    const take = sizes[i] as number;
    owners[pl.id] = positions.slice(cursor, cursor + take).sort((a, b) => a - b);
    cursor += take;
  });
  const clues = players.map((pl) => ({
    id: `vault.${pl.id}`,
    kind: 'vaultSymbols',
    holder: pl.role,
    value: (owners[pl.id] as number[]).map((pos) => ({ pos, symbol: code[pos] as number })),
  }));
  const par: VaultParams = { alphabet, code, owners };
  const st: VaultState = {
    entered: new Array<number | null>(CODE_LENGTH).fill(null),
    confirms: [],
    errors: 0,
    failed: false,
  };
  return {
    params: par,
    state: st,
    clues,
    solvers: ['mastermind', 'locksmith', 'hacker', 'lookout', 'driver'] as Role[],
  };
}

function apply(p: PuzzleInstance, act: PuzzleAct, ctx: ActContext) {
  const par = params(p);
  const st = state(p);
  if (st.failed) return failResult('illegalMove', 'the vault is locked');
  const actor = ctx.players.find((pl) => pl.id === ctx.actor);
  if (!actor || actor.expelled) return failResult('notAllowed', 'you are not on the keypad');

  if (act.kind === 'vault.set') {
    if (!Number.isInteger(act.pos) || act.pos < 0 || act.pos >= CODE_LENGTH) {
      return failResult('invalid', 'no such position');
    }
    if (!Number.isInteger(act.symbol) || act.symbol < 0 || act.symbol >= par.alphabet) {
      return failResult('invalid', 'no such symbol');
    }
    st.entered[act.pos] = act.symbol;
    st.confirms = [];
    return okResult({
      events: [{ type: 'vault.set', data: { pos: act.pos, by: ctx.actor } }],
    });
  }

  if (act.kind === 'vault.unconfirm') {
    st.confirms = st.confirms.filter((id) => id !== ctx.actor);
    return okResult({});
  }

  if (act.kind === 'vault.confirm') {
    if (st.entered.some((s) => s === null))
      return failResult('invalid', 'the code is not complete');
    if (!st.confirms.includes(ctx.actor)) st.confirms.push(ctx.actor);
    const everyone = ctx.players.filter((pl) => !pl.expelled);
    if (!everyone.every((pl) => st.confirms.includes(pl.id))) {
      return okResult({ events: [{ type: 'vault.confirm', data: { by: ctx.actor } }] });
    }
    st.confirms = [];
    const right = st.entered.every((s, i) => s === par.code[i]);
    if (right) return okResult({ solved: true, events: [{ type: 'vault.open', data: {} }] });
    st.errors += 1;
    if (st.errors > BALANCE.vaultErrorsAllowed) st.failed = true;
    return okResult({
      errorTenths: BALANCE.penalty.vault,
      undo: { kind: 'vaultError' },
      events: [{ type: 'vault.wrong', data: { errors: st.errors, locked: st.failed } }],
    });
  }
  return failResult('invalid', 'not a vault action');
}

function view(p: PuzzleInstance, ctx: ViewContext): Record<string, unknown> {
  const par = params(p);
  const st = state(p);
  const mine = p.clues.find((c) => c.holder === ctx.role);
  const base: Record<string, unknown> = {
    type: 'vault',
    solved: p.solved,
    length: CODE_LENGTH,
    alphabet: par.alphabet,
    entered: st.entered.slice(),
    confirms: st.confirms.slice(),
    errors: st.errors,
    locked: st.failed,
    // Only your own symbols: the point of the puzzle.
    mySymbols: mine ? shown(mine) : [],
    clues: {},
  };
  if (ctx.revealed) base['secret'] = { code: par.code, owners: par.owners };
  return base;
}

function legalActs(p: PuzzleInstance, _role: Role, ctx: ActContext): PuzzleAct[] {
  const par = params(p);
  const st = state(p);
  if (p.solved || st.failed) return [];
  const out: PuzzleAct[] = [];
  for (let pos = 0; pos < CODE_LENGTH; pos++) {
    for (let symbol = 0; symbol < par.alphabet; symbol++)
      out.push({ kind: 'vault.set', pos, symbol });
  }
  if (st.entered.every((s) => s !== null)) {
    out.push(
      st.confirms.includes(ctx.actor) ? { kind: 'vault.unconfirm' } : { kind: 'vault.confirm' },
    );
  }
  return out;
}

function tamper(p: PuzzleInstance, rng: Rng, exclude: Role | null): TamperChoice | null {
  const clues = alterableClues(p, exclude);
  if (clues.length === 0) return null;
  const clue = rng.pick(clues);
  const par = params(p);
  const list = (clue.value as { pos: number; symbol: number }[]).map((x) => ({ ...x }));
  const i = rng.int(0, list.length - 1);
  const item = list[i] as { pos: number; symbol: number };
  item.symbol = rng.pick(
    Array.from({ length: par.alphabet }, (_, s) => s).filter((s) => s !== item.symbol),
  );
  void clueByKind;
  return { clueId: clue.id, value: list };
}

function solve(raw: unknown): SolveInfo {
  const par = raw as VaultParams;
  const covered = new Set(Object.values(par.owners).flat());
  const solvable =
    covered.size === CODE_LENGTH && par.code.every((s) => s >= 0 && s < par.alphabet);
  return { solvable, difficulty: par.alphabet };
}

function autoSolve(p: PuzzleInstance): void {
  state(p).entered = params(p).code.slice();
}

function undo(p: PuzzleInstance, u: { kind: string }): void {
  if (u.kind !== 'vaultError') return;
  const st = state(p);
  st.errors = Math.max(0, st.errors - 1);
  if (st.errors <= BALANCE.vaultErrorsAllowed) st.failed = false;
}

export const vaultModule: PuzzleModule = {
  type: 'vault',
  generate,
  apply,
  view,
  legalActs,
  tamper,
  autoSolve,
  undo,
  solve,
};
